/**
 * Add / Rename Payment-method dialog (DNKADM-0091, §E) — a MUI `Dialog` that
 * creates an `external` method via `createPaymentMethod`, or renames any method
 * (including the protected Cash method) via `updatePaymentMethod`.
 *
 * Add mode collects a **label** for a new `external` method (kind is fixed —
 * admins add only external methods; `card` methods derive from connections, `cash`
 * is built-in). Rename mode edits only the **label** of the chosen method (its
 * kind/connection are immutable here).
 *
 * Client validation blocks Save while the label is empty. A duplicate-label `409`
 * (or a validation `400`) renders INLINE; anything else raises an error toast
 * (`classifyPaymentMethodsError`).
 */
import { useId, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  createPaymentMethod,
  updatePaymentMethod,
  type PaymentMethod,
} from '@/api/paymentMethods';
import type { ShowToast } from '@/components/toastContext';
import { classifyPaymentMethodsError } from './paymentMethodsError';

export interface PaymentMethodDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The method being renamed, or `null` to add a new `external` method. */
  method: PaymentMethod | null;
  /** Close the dialog without saving. */
  onClose: () => void;
  /** Called after a successful create/rename so the caller can refetch the list. */
  onSaved: () => void;
  /** Raise a non-blocking toast (upstream-error). */
  showToast: ShowToast;
}

export function PaymentMethodDialog(props: PaymentMethodDialogProps) {
  // Remount the inner form per open / per edited method so its input reseeds.
  if (!props.open) return null;
  return <PaymentMethodDialogForm key={props.method?.id ?? 'new'} {...props} />;
}

function PaymentMethodDialogForm({
  method,
  onClose,
  onSaved,
  showToast,
}: Omit<PaymentMethodDialogProps, 'open'>) {
  const ids = useId();
  const isRename = method !== null;

  const [label, setLabel] = useState(method?.label ?? '');
  const [labelError, setLabelError] = useState<string | undefined>(undefined);
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setServerError(null);
    const trimmed = label.trim();
    if (trimmed === '') {
      setLabelError('Enter a name.');
      return;
    }
    setLabelError(undefined);

    setSaving(true);
    try {
      if (isRename && method) await updatePaymentMethod(method.id, { label: trimmed });
      else await createPaymentMethod({ label: trimmed, kind: 'external' });
      // DNKADM-0320: confirm the save (after the write resolves; one toast per action).
      showToast(isRename ? 'Payment method renamed' : 'Payment method added', 'success');
      onSaved();
    } catch (err) {
      const view = classifyPaymentMethodsError(
        err,
        'The payment method couldn’t be saved. Please try again.',
      );
      if (view.kind === 'inline') setServerError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const titleId = `${ids}-title`;

  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>
        {isRename ? 'Rename payment method' : 'Add payment method'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {!isRename && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Add a custom payment method (for example Venmo or PayPal). It appears in the
              Add-Member payment dropdown alongside Cash and any card connections.
            </Typography>
          )}
          <TextField
            label="Name"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            disabled={saving}
            size="small"
            required
            error={Boolean(labelError)}
            helperText={labelError}
            slotProps={{ htmlInput: { 'aria-label': 'Payment method name' } }}
          />

          {serverError && (
            <Alert severity="error" role="alert">
              {serverError}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : isRename ? 'Save name' : 'Add method'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
